import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconDownload, IconRefresh, IconPause, IconPlay, IconShare, IconInfo,
  IconUpload, IconTrash, IconX, IconPlus,
} from './Icons.jsx';
import { CopyButton } from './CopyButton.jsx';
import { todayStr, fmtBytes } from './util.js';
import { api } from '../api.js';

const APPEARANCE_COLORS = [
  '#7c3aed', '#635bff', '#0abf53', '#00b0ff', '#ff4f88', '#8a3ffc',
  '#e2356a', '#1e5fa4', '#00c2a8', '#ff6a3d', '#0ea5e9', '#f59e0b',
  '#10b981', '#ef4444', '#6366f1', '#242935',
];

export function EndpointBar({ endpoint, baseUrl, count, events, onToggleStreaming, onRegenerate, onUpdate }) {
  const url = `${baseUrl}/u/${endpoint.id}`;
  const [confirming, setConfirming] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const streaming = !endpoint.paused;

  return (
    <div style={{
      margin: '16px 24px 0', padding: '14px 18px',
      background: '#fff', border: '1px solid var(--border-default)', borderRadius: 12,
      display: 'flex', alignItems: 'center', gap: 18,
    }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <button
          onClick={() => setAppearanceOpen(o => !o)}
          title="Customize endpoint appearance"
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: endpoint.color, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700, letterSpacing: '-0.04em',
            border: 0, padding: 0, cursor: 'pointer',
            position: 'relative', overflow: 'hidden',
            backgroundImage: endpoint.image ? `url(${endpoint.image})` : 'none',
            backgroundSize: 'cover', backgroundPosition: 'center',
          }}>
          {!endpoint.image && endpoint.glyph}
        </button>
        {appearanceOpen && (
          <AppearancePopover endpoint={endpoint} onUpdate={onUpdate} onClose={() => setAppearanceOpen(false)} />
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-1)' }}>{endpoint.name}</span>
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>endpoint</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <code style={{
            fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 500,
            color: 'var(--fg-1)', background: 'var(--neutral-25)',
            padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)',
            userSelect: 'all', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            minWidth: 0,
          }}>{url}</code>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 4, padding: 2, background: 'var(--neutral-25)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm icon-only" onClick={() => setDownloadOpen(o => !o)} title="Download captured events">
                <IconDownload size={14} />
              </button>
              {downloadOpen && (
                <DownloadPopover endpoint={endpoint} events={events} onClose={() => setDownloadOpen(false)} />
              )}
            </div>
            <CopyButton text={url} iconOnly title="Copy URL" />
            {confirming ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', marginLeft: 4, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>Old URL stops working.</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={() => { onRegenerate(); setConfirming(false); }}>Regenerate</button>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm icon-only" onClick={() => setConfirming(true)} title="Regenerate URL — old URL stops working">
                <IconRefresh size={14} />
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm icon-only"
              onClick={onToggleStreaming}
              title={streaming ? 'Pause this endpoint' : 'Resume this endpoint'}
              style={{ position: 'relative', color: streaming ? '#166534' : 'var(--fg-2)' }}
            >
              {streaming ? <IconPause size={14} /> : <IconPlay size={14} />}
              {streaming && <span className="pulse-dot" style={{ position: 'absolute', top: 4, right: 4 }} />}
            </button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm icon-only" onClick={() => setShareOpen(o => !o)} title="Get a read-only share link">
                <IconShare size={14} />
              </button>
              {shareOpen && (
                <SharePopover endpoint={endpoint} onClose={() => setShareOpen(false)} />
              )}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 22, paddingLeft: 18, borderLeft: '1px solid var(--border-default)', flexShrink: 0 }}>
        <Stat label="Received" value={count.toLocaleString()} tooltip="Total events captured on this endpoint in the last 7 days." />
        <Stat label="Retention" value="7 days" tooltip="How long captured events are kept before being purged. Pinned events are kept indefinitely." />
      </div>
    </div>
  );
}

function Stat({ label, value, tooltip }) {
  return (
    <div style={{ position: 'relative' }} title={tooltip}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {tooltip && <IconInfo size={11} stroke="var(--fg-4)" />}
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--fg-1)', letterSpacing: '-0.015em', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function AppearancePopover({ endpoint, onUpdate, onClose }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [glyphDraft, setGlyphDraft] = useState(endpoint.glyph);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const onPickImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onUpdate({ image: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 50,
      width: 320, background: '#fff',
      border: '1px solid var(--border-default)', borderRadius: 12,
      boxShadow: 'var(--shadow-lg)', padding: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>Endpoint appearance</div>
        <button className="btn btn-ghost icon-only btn-sm" onClick={onClose}><IconX size={12}/></button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: 10, background: 'var(--neutral-25)', borderRadius: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: endpoint.color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 700, letterSpacing: '-0.04em',
          backgroundImage: endpoint.image ? `url(${endpoint.image})` : 'none',
          backgroundSize: 'cover', backgroundPosition: 'center',
          flexShrink: 0,
        }}>{!endpoint.image && endpoint.glyph}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>{endpoint.name}</div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>/{endpoint.id}</div>
        </div>
        {endpoint.image && (
          <button className="btn btn-ghost btn-sm" onClick={() => onUpdate({ image: null })} title="Remove image">
            <IconTrash size={12}/>
          </button>
        )}
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Color</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, marginBottom: 14 }}>
        {APPEARANCE_COLORS.map(c => {
          const active = endpoint.color === c;
          return (
            <button key={c} onClick={() => onUpdate({ color: c })} title={c} style={{
              width: '100%', aspectRatio: '1 / 1', borderRadius: 7,
              background: c, border: active ? '2px solid var(--fg-1)' : '2px solid transparent',
              boxShadow: active ? '0 0 0 2px #fff inset' : 'none',
              cursor: 'pointer', padding: 0,
            }} />
          );
        })}
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Letter</div>
      <input
        value={glyphDraft}
        maxLength={2}
        onChange={(e) => {
          const v = e.target.value.toUpperCase().slice(0, 2);
          setGlyphDraft(v);
          if (v) onUpdate({ glyph: v });
        }}
        style={{
          width: '100%', padding: '8px 10px',
          border: '1px solid var(--border-default)', borderRadius: 8,
          fontSize: 13, fontFamily: 'inherit', outline: 'none', marginBottom: 14,
          textTransform: 'uppercase',
        }}
        placeholder="1–2 chars"
      />

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Custom image</div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickImage} />
      <button
        onClick={() => fileRef.current && fileRef.current.click()}
        style={{
          width: '100%', padding: '10px 12px',
          background: '#fff', border: '1px dashed var(--border-default)', borderRadius: 8,
          fontSize: 12.5, color: 'var(--fg-2)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: 'inherit',
        }}>
        <IconUpload size={14} />
        {endpoint.image ? 'Replace image' : 'Upload PNG, JPG or SVG'}
      </button>
      <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 6, lineHeight: 1.4 }}>
        Image overrides the letter. 1:1 aspect crop.
      </div>
    </div>
  );
}

function SharePopover({ endpoint, onClose }) {
  const ref = useRef(null);
  const [token, setToken] = useState(endpoint.shareToken || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const create = async () => {
    setBusy(true);
    try { const r = await api.shareEndpoint(endpoint.id); setToken(r.shareToken); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await api.revokeShare(endpoint.id); setToken(null); }
    finally { setBusy(false); }
  };

  const shareUrl = token ? `${window.location.origin}/share/${token}` : null;

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
      width: 360, background: '#fff',
      border: '1px solid var(--border-default)', borderRadius: 12,
      boxShadow: 'var(--shadow-lg)', padding: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <span style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: 'var(--yuno-purple-50)', color: 'var(--yuno-purple-700)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}><IconShare size={14} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>Share {endpoint.name} feed</div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.4 }}>
            Read-only link. Recipients see incoming events but can't modify or delete.
          </div>
        </div>
      </div>
      {shareUrl ? (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <code style={{
              flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 11.5,
              color: 'var(--fg-1)', background: 'var(--neutral-25)',
              padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border-subtle)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'all',
            }}>{shareUrl}</code>
            <CopyButton text={shareUrl} label="Copy" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: 'var(--fg-3)' }}>
            <span>Active link · revoke anytime</span>
            <button className="btn btn-ghost btn-sm" onClick={revoke} disabled={busy}>Revoke</button>
          </div>
        </>
      ) : (
        <button className="btn btn-primary" onClick={create} disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Generating…' : 'Generate share link'}
        </button>
      )}
    </div>
  );
}

const DOWNLOAD_RANGES = [
  { key: 'today', label: 'Today', desc: 'Since 00:00 today' },
  { key: '24h', label: 'Last 24 hours', desc: 'Rolling 24h window' },
  { key: '7d', label: 'Last 7 days', desc: 'Full retention period' },
  { key: 'all', label: 'All captured', desc: 'Everything currently in the inbox' },
  { key: 'custom', label: 'Custom range', desc: 'Pick start and end dates' },
];
const DOWNLOAD_FORMATS = [
  { key: 'json', label: 'JSON', desc: 'Single array' },
  { key: 'ndjson', label: 'NDJSON', desc: 'One event per line' },
  { key: 'csv', label: 'CSV', desc: 'Flat — id, method, received, status, ip' },
];

function DownloadPopover({ endpoint, events, onClose }) {
  const ref = useRef(null);
  const [range, setRange] = useState('today');
  const [format, setFormat] = useState('json');
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    let lo = -Infinity, hi = Infinity;
    if (range === 'today') lo = startOfToday.getTime();
    else if (range === '24h') lo = now - 24 * 60 * 60 * 1000;
    else if (range === '7d') lo = now - 7 * 24 * 60 * 60 * 1000;
    else if (range === 'custom') {
      lo = new Date(from + 'T00:00:00').getTime();
      hi = new Date(to + 'T23:59:59').getTime();
    }
    return events.filter(e => e.receivedAt >= lo && e.receivedAt <= hi);
  }, [events, range, from, to]);

  const sizeBytes = useMemo(() => {
    if (format === 'json') return JSON.stringify(filtered, null, 2).length;
    if (format === 'ndjson') return filtered.map(e => JSON.stringify(e)).join('\n').length;
    return filtered.length * 120;
  }, [filtered, format]);

  const onDownload = () => {
    if (filtered.length === 0) return;
    // Drive a download from the server so we get the freshest data and don't
    // rely on the in-memory event cache being complete.
    const url = api.downloadUrl(endpoint.id, format, range, from, to);
    const a = document.createElement('a');
    a.href = url;
    document.body.appendChild(a); a.click(); a.remove();
    onClose();
  };

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
      width: 380, background: '#fff',
      border: '1px solid var(--border-default)', borderRadius: 12,
      boxShadow: 'var(--shadow-lg)', padding: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
        <span style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: 'var(--yuno-purple-50)', color: 'var(--yuno-purple-700)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}><IconDownload size={14} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>Download events</div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2 }}>{endpoint.name} endpoint</div>
        </div>
        <button className="btn btn-ghost icon-only btn-sm" onClick={onClose}><IconX size={12}/></button>
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Range</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
        {DOWNLOAD_RANGES.map(r => (
          <label key={r.key} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
            border: '1px solid', borderColor: range === r.key ? 'var(--yuno-purple-300)' : 'var(--border-subtle)',
            background: range === r.key ? 'var(--yuno-purple-50)' : '#fff',
            borderRadius: 8, cursor: 'pointer',
          }}>
            <input type="radio" name="range" checked={range === r.key} onChange={() => setRange(r.key)} style={{ margin: 0, accentColor: 'var(--yuno-purple-600)' }}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--fg-1)' }}>{r.label}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{r.desc}</div>
            </div>
          </label>
        ))}
      </div>

      {range === 'custom' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              style={{ width: '100%', marginTop: 4, padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }}/>
          </label>
          <label style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              style={{ width: '100%', marginTop: 4, padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }}/>
          </label>
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Format</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 14 }}>
        {DOWNLOAD_FORMATS.map(f => (
          <button key={f.key} onClick={() => setFormat(f.key)} style={{
            padding: '8px 10px', textAlign: 'left',
            border: '1px solid', borderColor: format === f.key ? 'var(--yuno-purple-300)' : 'var(--border-subtle)',
            background: format === f.key ? 'var(--yuno-purple-50)' : '#fff',
            borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-1)' }}>{f.label}</div>
            <div style={{ fontSize: 10.5, color: 'var(--fg-3)', marginTop: 1 }}>{f.desc}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--neutral-25)', borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 12 }}>
          <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>{filtered.length.toLocaleString()}</span>
          <span style={{ color: 'var(--fg-3)' }}> event{filtered.length === 1 ? '' : 's'} · ~{fmtBytes(sizeBytes)}</span>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onDownload} disabled={filtered.length === 0}>
          <IconDownload size={13}/> Download .{format}
        </button>
      </div>
    </div>
  );
}
